// ============================================================
// CE QUE L'AGENT A LU SUR LE PLAN — 22 août 2026.
// ------------------------------------------------------------
// Charles : « j'aimerais pouvoir voir quand une pièce est lue par le
// programme. J'aimerais voir la section lue — longueur, largeur ou
// diamètre — et la profondeur réelle lue. Ça devrait être noté pour voir
// si l'agent a bien lu la pièce. On devrait même pouvoir détailler avec
// une flèche pour voir toutes les cotes lues. Ici ça reste une boîte
// noire. »
//
// ── CE QUE CE BLOC RÉPARE, ET CE N'EST PAS QU'UN AFFICHAGE ──
//
// L'écran montrait le BRUT — des cotes calculées, majorées, arrondies —
// et nulle part ce qui avait été LU. Sur « Bloc à souder » (devis 048),
// l'agent avait lu 35, 50, 50 et 13 sans savoir laquelle était
// l'épaisseur : la pièce était déclarée incalculable, entièrement cotée,
// et personne ne pouvait le voir.
//
// ── LA DISTINCTION QUE CE BLOC REND VISIBLE ──
//
//   RETENU  — les cotes rangées dans les trois cases, celles qui
//             calculent le brut, le volume et le prix
//   LU      — tout ce qui est écrit sur le papier, y compris ce qui
//             n'a pas trouvé sa case
//
// Une cote lue et non retenue n'est pas une faute : c'est une question
// que le deviseur tranche en un clic. Encore faut-il qu'elle s'affiche.
// ============================================================

import { useState } from 'react';
import Badge from '../../ui/Badge.jsx';
import { patchCoteRetenue } from './cotesDerivees.js';
import Button from '../../ui/Button.jsx';
import NumberField from '../../ui/NumberField.jsx';
import { formatNumber } from '../../lib/format.js';

/** Les trois cases retenues, dans le vocabulaire de la forme lue. */
function sectionRetenue(piece) {
  const g = piece.geometry;
  const part = g?.part;
  if (!part) return null;
  if (g.partShape === 'cylinder') {
    return [
      { label: 'Ø', valeur: part.diameter },
      // `h` : repli pour les géométries où l'écran rangeait la profondeur
      // d'une pièce ronde au mauvais endroit (avant le 22/10/2026). Le
      // serveur lit la même chose (`cotesDeLaPiece`).
      { label: 'Profondeur', valeur: part.length ?? part.h },
    ];
  }
  return [
    { label: 'Longueur', valeur: part.l },
    { label: 'Largeur', valeur: part.w },
    { label: 'Profondeur', valeur: part.h },
  ];
}

function Cote({ label, valeur, onRetenir, saisie = false }) {
  const absente = !(Number(valeur) > 0);
  return (
    <div className="cotelue">
      <p className="caps">{label}</p>
      {absente ? (
        // « — » et non « 0 » : personne n'a lu zéro. C'est l'invariant 5,
        // à l'endroit où il se voit.
        <p className="num cotelue__valeur cotelue__valeur--absente">
          non lue
          {onRetenir ? null : ''}
        </p>
      ) : (
        <p className="num cotelue__valeur">
          {formatNumber(valeur)} mm {saisie ? <Badge tone="neutral">saisie</Badge> : null}
        </p>
      )}
    </div>
  );
}

/**
 * Les cotes des USINAGES, tirées des entités relevées.
 *
 * Charles, sur « REGLAGE PORTE » : « il ne relève que 5 cotes au lieu
 * de 9 ». En vérifiant, l'agent avait bien lu Ø10,6, Ø4,70 et Ø28,5 —
 * mais rangés dans les ENTITÉS, et cet écran ne montrait que les cotes
 * d'encombrement. Le relevé était meilleur que ce qu'on en voyait.
 *
 * Une preuve de lecture qui cache la moitié de ce qui a été lu ne prouve
 * rien : elle donne l'impression d'un agent distrait.
 */
function cotesDesUsinages(piece) {
  const out = [];
  for (const f of piece.geometry?.features ?? []) {
    const n = Number(f.count) > 1 ? `${f.count} × ` : '';
    const quoi = String(f.kind ?? 'usinage').replace(/_/g, ' ');
    for (const [label, v] of [
      ['Ø', f.diameter], ['Ø2', f.diameter2],
      ['longueur', f.l], ['largeur', f.w], ['profondeur', f.depth],
    ]) {
      if (Number(v) > 0) out.push({ valeur_mm: Number(v), repere: `${n}${quoi} — ${label}` });
    }
  }
  return out;
}

export function CotesLues({ piece, disabled, onChange }) {
  const [retenue, setRetenue] = useState(null);
  const [saisies, setSaisies] = useState(() => new Set());
  const identite = piece.id ?? piece.ref_interne ?? piece.name;
  const retenues = sectionRetenue(piece);
  const lues = piece.geometry?.cotes_lues ?? [];
  const usinages = cotesDesUsinages(piece);
  if (!retenues && lues.length === 0 && usinages.length === 0) return null;

  // Une cote LUE qui n'a pas été retenue dans les trois cases. C'est
  // elle qui explique un brut incalculable sur une pièce cotée.
  const valeursRetenues = new Set(
    (retenues ?? []).map((c) => Number(c.valeur)).filter((v) => v > 0),
  );
  const orphelines = lues.filter((c) => !valeursRetenues.has(Number(c.valeur_mm)));
  const manquantes = (retenues ?? []).filter((c) => !(Number(c.valeur) > 0));

  // ── CE QUI VIENT D'ÊTRE RETENU SE DIT — 2026-10-027, 22/10/2026 ──
  // Retenu au COMMIT (Entrée, perte du focus) : à la frappe, le premier
  // « 1 » remplissait la case et le champ disparaissait sous les doigts.

  /** Ranger une cote lue, et effacer le brut qui en dérive. */
  function retenir(labelCase, valeur, origine = 'relevée sur le plan') {
    if (!(Number(valeur) > 0)) return;
    const patch = patchCoteRetenue(piece, labelCase, valeur);
    if (!patch) return;
    onChange(patch);
    // Le message porte l'identité de SA pièce : changer de pièce ne doit
    // pas le laisser affiché sous une autre (§11, ligne 13).
    setRetenue({ pour: identite, texte: `${labelCase} ${formatNumber(valeur)} mm retenue (${origine})` });
    setSaisies((s) => {
      const n = new Set(s);
      if (origine === 'saisie') n.add(`${identite}:${labelCase}`); else n.delete(`${identite}:${labelCase}`);
      return n;
    });
  }

  return (
    <div className="cotes-lues">
      <div className="row row--between">
        <p className="caps">Ce que l’agent a lu — encombrement</p>
        {lues.length + usinages.length ? (
          <Badge tone="neutral">{lues.length + usinages.length} cotes relevées</Badge>
        ) : null}
      </div>

      {retenues ? (
        <div className="cotes-lues__section">
          {retenues.map((c) => (
            <Cote key={c.label} label={c.label} valeur={c.valeur} saisie={saisies.has(`${identite}:${c.label}`)} />
          ))}
        </div>
      ) : (
        <p className="field__hint">
          Aucune cote n’a été rangée : la forme de la pièce n’a pas été tranchée.
        </p>
      )}

      {/* ── UNE COTE QUI MANQUE SE DONNE — 25 août 2026 ──
          « BLOC SUPPORT » (2026-08-067) : la largeur n'est cotée nulle
          part, aucun bouton n'aurait la bonne valeur. **Refuser d'inventer
          n'est pas refuser de recevoir** : le champ est TOUJOURS là quand
          une case est vide. Ce qu'on y écrit est `saisi` — la plus haute
          provenance — et redescend dans toute la chaîne : brut, volume,
          copeaux, masse, prix (CLAUDE.md §2bis). */}
      {retenue?.pour === identite ? (
        <p className="field__hint" role="status">
          <strong>{retenue.texte}</strong> — le brut, la masse et le prix sont recalculés.
        </p>
      ) : null}

      {manquantes.length ? (
        <div className="cotes-lues__arbitrage">
          <p className="field__hint">
            <strong>
              {manquantes.length > 1
                ? `${manquantes.length} cotes manquent pour calculer le brut`
                : `« ${manquantes[0].label} » manque pour calculer le brut`}
            </strong>{' '}
            {orphelines.length
              ? `— l’agent a relevé ${orphelines.length} ${
                orphelines.length > 1 ? 'autres cotes' : 'autre cote'
              } sans savoir quel rôle leur donner. Choisissez, ou saisissez la vôtre :`
              : '— elle n’est cotée nulle part sur le plan. Saisissez-la :'}
          </p>
          {manquantes.map((c) => (
            <div key={c.label} className="cotes-lues__choix">
              <NumberField
                label={c.label}
                unit="mm"
                min={0}
                value={null}
                disabled={disabled}
                hint="Mesurée sur le plan, calculée, ou demandée au client. Entrée pour valider."
                onCommit={(v) => retenir(c.label, v, 'saisie')}
              />
              {orphelines.length ? (
                <div className="row row--serre cotes-lues__pistes">
                  <span className="field__hint">Relevées sur le plan :</span>
                  {orphelines.map((o) => (
                    <Button
                      key={o.valeur_mm}
                      size="sm"
                      variant="secondary"
                      disabled={disabled}
                      onClick={() => retenir(c.label, o.valeur_mm)}
                    >
                      {formatNumber(o.valeur_mm)} mm
                      {o.repere ? ` · ${o.repere}` : ''}
                    </Button>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {/* ── LES VUES, pour vérifier la lecture plan en main ──
          « 140 » ne se vérifie pas. « 140, cote hors-tout au-dessus de la
          vue de face » se vérifie en trois secondes. C'est la réponse à
          « je veux voir si l'agent a bien lu la pièce ». */}
      {(piece.geometry?.vues ?? []).length ? (
        <details className="repli">
          <summary>Les vues relevées sur le plan ({piece.geometry.vues.length})</summary>
          <table className="cotes-lues__table">
            <thead>
              <tr>
                <th scope="col">Vue</th>
                <th scope="col">Hors-tout</th>
                <th scope="col">D’où vient la cote</th>
              </tr>
            </thead>
            <tbody>
              {piece.geometry.vues.map((v, i) => (
                <tr key={`${v.nom}-${i}`}>
                  <td>{v.nom || '—'}</td>
                  <td className="num">
                    {v.horizontal_mm ? `${formatNumber(v.horizontal_mm)}` : '—'}
                    {' × '}
                    {v.vertical_mm ? `${formatNumber(v.vertical_mm)}` : '—'}
                    {' mm'}
                  </td>
                  <td>{[v.horizontal_source, v.vertical_source].filter(Boolean).join(' · ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="field__hint">
            L’enveloppe retenue vient de ces vues, et d’elles seules. Une cote qui
            n’est l’extrémité d’aucune vue est écartée — c’est souvent un entraxe.
          </p>
        </details>
      ) : null}

      {/* Les cotes des USINAGES. Elles étaient lues et invisibles. */}
      {usinages.length ? (
        <details className="repli">
          <summary>Les cotes des usinages relevées ({usinages.length})</summary>
          <table className="cotes-lues__table">
            <tbody>
              {usinages.map((c, i) => (
                <tr key={`${c.valeur_mm}-${i}`}>
                  <td className="num">{formatNumber(c.valeur_mm)} mm</td>
                  <td>{c.repere}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      ) : null}

      {/* Toutes les cotes lues, derrière une flèche. « Ici ça reste une
          boîte noire » : c'est la porte qui l'ouvre. Repliée par défaut —
          on ne la consulte que lorsqu'on doute — et OUVERTE d'office
          quand une case est restée vide, parce que c'est là qu'on doute. */}
      {lues.length ? (
        <details className="repli" open={manquantes.length > 0}>
          <summary>
            Toutes les cotes relevées sur le plan ({lues.length})
          </summary>
          <table className="cotes-lues__table">
            <thead>
              <tr>
                <th scope="col">Cote</th>
                <th scope="col">Ce qu’elle mesure</th>
                <th scope="col">Où elle est lue</th>
              </tr>
            </thead>
            <tbody>
              {lues.map((c) => (
                <tr
                  key={`${c.valeur_mm}-${c.repere}`}
                  className={valeursRetenues.has(Number(c.valeur_mm)) ? 'est-retenue' : undefined}
                >
                  <td className="num">{formatNumber(c.valeur_mm)} mm</td>
                  <td>{c.repere || '—'}</td>
                  <td>{c.evidence || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="field__hint">
            Les lignes en gras sont celles retenues pour le calcul. Les autres sont
            lues sur le plan sans avoir trouvé leur place — elles ne sont pas perdues.
          </p>
        </details>
      ) : null}
    </div>
  );
}

export default CotesLues;
